
import { catchError, mergeMap, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { MailingListModel } from '../models/mailingList.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { MailingListsApi } from '../../../api/mailingLists/mailingLists.api';
import { RecipientModel } from '../models/recipient.model';
import { RecipientSearchResultsModel } from '../models/recipient-search-results.model';
import { download } from '../../Misc/helpers/download/download.helper';

@Injectable()
export class MailingListsService {
    /**
     * Flag for if the mailing lists have been fetched yet
     * The purpose is to make it so if you don't go to an area that needs mailing lists,
     *   but the service exists globally, then it won't generate traffic, until it's needed
     */
    private hasFetchedMailingLists: boolean = false;

    private mailingLists: MailingListModel[] = null;
    private mailingLists$: BehaviorSubject<MailingListModel[]> = new BehaviorSubject<MailingListModel[]>(this.mailingLists);

    constructor(
        private mailingListsApi: MailingListsApi
    ) {}

    private fetchAllMailingLists(): Observable<MailingListModel[]> {
        this.hasFetchedMailingLists = true;
        const request = this.mailingListsApi.getAllMailingLists().pipe(
            map(apiModels => apiModels.map(apiModel => new MailingListModel(apiModel))));

        request.subscribe((mailingLists) => {
            this.setMailingLists(mailingLists);
        });

        return request;
    }

    refreshMailingLists(): Observable<MailingListModel[]> {
        // TODO add a throttle?
        return this.fetchAllMailingLists();
    }

    private setMailingLists(mailingLists: MailingListModel[]): void {
        this.mailingLists = mailingLists;
        this.mailingLists$.next(this.mailingLists);
    }

    getMailingLists(): Observable<MailingListModel[]> {
        if (!this.hasFetchedMailingLists) {
            this.fetchAllMailingLists();
        }
        return this.mailingLists$;
    }

    createMailingList(mailingList: MailingListModel): Observable<MailingListModel> {
        let id: number;
        return this.mailingListsApi.createMailingList(mailingList.getCreateApiModel()).pipe(
            mergeMap(newId => {
                id = newId;
                return this.fetchAllMailingLists();
            }),
            map(mailingLists => {
                return mailingLists.find(list => list.id === id);
            }),
            catchError((err) => {
                // TODO: error growl message

                throw err;
            })
        );
    }

    updateMailingList(mailingList: MailingListModel, removal: boolean = false): Observable<MailingListModel> {
        return this.mailingListsApi.updateMailingList(mailingList.getUpdateApiModel(), removal).pipe(
            mergeMap(() => this.fetchAllMailingLists()),
            map(mailingLists => {
                return mailingLists.find(list => list.id === mailingList.id);
            })
        );
    }

    deleteMailingList(mailingList: MailingListModel): Observable<void> {
        return this.mailingListsApi.deleteMailingList(mailingList.id).pipe(
            map(() => {
                this.fetchAllMailingLists();
            }),
            catchError((err) => {
                // TODO: error growl message

                throw err;
            })
        );
    }

    deleteRecipientsBulk(ids: number[]): Observable<void> {
        return this.mailingListsApi.deleteRecipientsBulk(ids).pipe(
            catchError((err) => {
                throw err;
            })
        );
    }

    deleteAllRecipients(): Observable<void> {
        return this.mailingListsApi.deleteAllRecipients().pipe(
            catchError((err) => {
                throw err;
            })
        );
    }

    deleteRecipientsByMailingList(mailingListId: number): Observable<void> {
        return this.mailingListsApi.deleteRecipientsByMailingList(mailingListId).pipe(
            catchError((err) => {
                throw err;
            })
        );
    }

    exportMailingList(mailingList: MailingListModel) {
        const request = this.mailingListsApi.exportMailingList(mailingList.id);
        request.subscribe((csvString) => {
            download.blob(download.getCSVBlob(csvString), mailingList.name + '.csv');
        });
    }

    getRecipientsByIds(ids: number[]): Observable<RecipientModel[]> {
        return this.mailingListsApi.getRecipientsByIds(ids).pipe(
            map(apiModels => apiModels.map(apiModel => new RecipientModel(apiModel))));
    }

    getRecipientsForMailingList(mailingList: MailingListModel): Observable<RecipientModel[]> {
        return this.getRecipientsByIds(mailingList.recipientIds);
    }

    getRecipientById(id: number): Observable<RecipientModel> {
        return this.mailingListsApi.getRecipientById(id).pipe(
            map(apiModel => new RecipientModel(apiModel)));
    }

    createRecipient(recipient: RecipientModel): Observable<RecipientModel> {
        return this.mailingListsApi.createRecipient(recipient.getCreateApiModel()).pipe(
            mergeMap(id => this.getRecipientById(id))
        );
    }

    updateRecipient(recipient: RecipientModel): Observable<RecipientModel> {
        return this.mailingListsApi.updateRecipient(recipient.getUpdateApiModel()).pipe(
            mergeMap(() => this.getRecipientById(recipient.id))
        );
    }

    deleteRecipient(recipient: RecipientModel): Observable<void> {
        return this.mailingListsApi.deleteRecipient(recipient.id).pipe(
            catchError((err) => {
                // TODO: error growl message

                throw err;
            })
        );
    }

    searchRecipients(query: string, pageNumber: number = 0, pageSize: number = 25): Observable<RecipientSearchResultsModel> {
        return this.mailingListsApi.searchRecipients(query, pageNumber, pageSize).pipe(
            map(results => new RecipientSearchResultsModel(results)));
    }

    importRecipients(recipients: RecipientModel[], list?: MailingListModel): Observable<void> {
        return this.mailingListsApi.importRecipients(
            recipients.map(recipient => recipient.getCreateApiModel()),
            list && list.getUpdateApiModel()
        ).pipe(
            map(() => {
                this.fetchAllMailingLists();
            })
        );
    }

    getRecipientCount(mailingListIds: number[]): number {
        let count = 0;
        this.mailingLists?.forEach(mailingList => {
            if (mailingListIds?.includes(mailingList.id)) {
                if (mailingList.count) {
                    count += mailingList.count;
                }
            }
        });

        return count;
    }
}
